import React, { ReactNode, ReactElement, Ref } from 'react';

import Checkbox from '@adsk/alloy-react-checkbox';
import theme from '@adsk/alloy-react-theme';

import { getLabel, isColumnDisabled } from './helpers';
import { TableConfigurationMenuColumnInstance } from './types';

export type ColumnOptionProps<D extends Record<string, unknown>> = {
  children?: ReactNode;
  column: TableConfigurationMenuColumnInstance<D>;
  handleToggleColumn: (column: TableConfigurationMenuColumnInstance<D>) => void;
};

const ColumnOption = <D extends Record<string, unknown>>(
  { column, children, handleToggleColumn, ...props }: ColumnOptionProps<D>,
  ref: Ref<HTMLLabelElement>
) => {
  const disabled = isColumnDisabled(column);
  return (
    <label
      ref={ref}
      css={[
        theme.typography.bodyMedium,
        {
          display: column.hiddenInMenu ? 'none' : 'flex',
          alignItems: 'center',
          padding: '10px 24px',
          paddingLeft: children ? 8 : 24,
          ...(disabled
            ? { color: theme.colors.charcoal500 }
            : {
                cursor: 'pointer',
                ':hover': {
                  backgroundColor: theme.colors.charcoal050,
                },
              }),
        },
      ]}
      {...props}
    >
      {children}
      <Checkbox
        checked={column.isVisible}
        disabled={disabled}
        onChange={() => handleToggleColumn(column)}
        style={{ marginRight: 12 }}
      />
      <span>{getLabel<D>(column)}</span>
    </label>
  );
};

export default React.forwardRef(ColumnOption) as <
  D extends Record<string, unknown>
>(
  p: ColumnOptionProps<D> & { ref?: Ref<HTMLLabelElement> }
) => ReactElement;
